import Checkbox from '../../Checkbox/checkbox'
import { useDataTableContext } from '../../DataTable'
import { OrderableList } from '../../OrderableList/OrderableList'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { RiGridFill } from 'react-icons/ri'
import styled from 'styled-components'
import { IColumnVisibility } from '../../DataTable/types'
import TableButton from '../../Buttons/TableButtons/TableButton'
import { HiOutlineViewColumns } from 'react-icons/hi2'
import { Modal } from '../../UI/Modal/Modal'

function ColumnItem({
  isDragging,
  label,
  isChecked,
  onChange,
  disabled,
}: {
  isDragging?: boolean
  label: string
  isChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
}) {
  const [checked, setChecked] = useState(isChecked)
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked)
    onChange?.(e.target.checked)
  }

  const toggleChecked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    checkBoxRef.current?.click()
  }

  return (
    <ColumnItemWrapper isDragging={isDragging}>
      <ColumnItemLabel checked={checked} onClick={toggleChecked}>
        <Checkbox
          ref={checkBoxRef}
          checked={checked}
          onChange={disabled ? undefined : handleChange}
        />
        {label}
      </ColumnItemLabel>
      <RiGridFill
        color="#D0D5DD"
        size={21}
        style={disabled ? undefined : { cursor: 'grab' }}
      />
    </ColumnItemWrapper>
  )
}

const ColumnItemWrapper = styled.div<{ isDragging?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  ${({ isDragging }) =>
    isDragging &&
    `
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  `}
`

const ColumnItemLabel = styled.div<{ checked?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  ${({ checked }) =>
    !checked &&
    `
    color: #D0D5DD;
  `}
`

type HeaderItem<T> = {
  header: string
  columnId: keyof T
  isVisible: boolean
  isEditable: boolean
  isHidden?: boolean
}

export function ColumnsModal<T extends object>() {
  const [show, setShow] = useState(false)
  const [draggingKey, setDraggingKey] = React.useState<string | null>(null)

  const {
    state: { table: tableOriginal, columnVisibility, columnOrder },
    methods: { setColumnVisibility, setColumnOrder },
  } = useDataTableContext<T>()

  const [orderedHeaders, setOrderedHeaders] = useState<HeaderItem<T>[]>()

  const initializedRef = useRef(false)

  useEffect(() => {
    if (orderedHeaders == null && tableOriginal != null) {
      const copiedHeaders: HeaderItem<T>[] = []

      tableOriginal?.getAllColumns().forEach((c) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const meta = c.columnDef.meta as any
        if (!meta?.notSortable) {
          copiedHeaders.push({
            // Get the header title from the meta object if it exists
            // Otherwise, use the header value (which can be a string or a component)
            header: meta?.headerTitle ?? c.columnDef.header,
            columnId: c.id as keyof T,
            isVisible: columnVisibility?.[c.id as keyof T] ?? true,
            isEditable: meta?.isEditable ?? true,
            isHidden: c.columnDef.id === 'expanded-row',
          })
        }
      })

      const copiedOrderedHeaders = copiedHeaders.sort((a, b) => {
        const aIndex = columnOrder?.findIndex(
          (id) => String(id) === String(a.columnId)
        )
        const bIndex = columnOrder?.findIndex(
          (id) => String(id) === String(b.columnId)
        )
        if (aIndex == null || bIndex == null) {
          return 0
        }
        return aIndex - bIndex
      })

      setOrderedHeaders(copiedOrderedHeaders)
    }
  }, [tableOriginal])

  const setIsVisibleOnOrderedHeaders = (id: keyof T, isVisible: boolean) => {
    setOrderedHeaders((prev) => {
      return prev?.map((header) => {
        if (header.columnId === id) {
          header.isVisible = isVisible
          return header
        }
        return header
      }) as typeof orderedHeaders
    })
  }

  const handleSortChange = useCallback(
    (newOrder: string[]) => {
      // Order headers by using newOrder key
      const newHeaders = orderedHeaders?.sort((a, b) => {
        const aIndex = newOrder.findIndex(
          (id) => String(id) === String(a.columnId)
        )
        const bIndex = newOrder.findIndex(
          (id) => String(id) === String(b.columnId)
        )
        // If not found move to the end
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      })
      setOrderedHeaders(newHeaders)
    },
    [orderedHeaders]
  )

  const handleApply = () => {
    if (orderedHeaders) {
      setColumnOrder(orderedHeaders.map(({ columnId }) => String(columnId)))
      setColumnVisibility(
        orderedHeaders.reduce(
          (acc: IColumnVisibility<T>, { columnId, isVisible }) => {
            acc[columnId as keyof T] = isVisible
            return acc
          },
          {} as IColumnVisibility<T>
        )
      )
    }
    setShow(false)
  }

  // The purpose of this effect is to handle scenarios where the user has column sorting state
  // saved to their local storage, and a new column is introduced.
  // Be default we want to ensure the new column is the last item in
  //   (1) the orderedHeaders array (modal) and (2) the columnOrder array (table)
  // and before the actions column (if actions column is the last item). (See FIR-1064)
  useEffect(() => {
    if (initializedRef.current) return
    // Check if the effect should run (not initialized and orderedHeaders has items)
    if (orderedHeaders && columnOrder && orderedHeaders?.length > 0) {
      // Extract columnIds from orderedHeaders
      const currentHeaders = orderedHeaders.map(({ columnId }) =>
        String(columnId)
      )

      // Find missing keys in columnOrder (from localStorage cache)
      const missingKeys = currentHeaders.filter(
        (key) => !columnOrder.includes(String(key))
      )

      // If there are missing keys
      if (missingKeys.length) {
        const newColumnOrder = [...columnOrder]
        const newOrderedHeaders = [...orderedHeaders]

        // Find the index of 'actions' in newColumnOrder
        const actionsIndex = newColumnOrder.indexOf('actions')

        // Calculate the index where missing keys should be inserted
        // If 'actions' is the last item, insert missing keys before 'actions', otherwise insert at the end
        const insertIndex =
          actionsIndex === newColumnOrder.length - 1
            ? actionsIndex
            : newColumnOrder.length

        // Insert missing keys into newColumnOrder at the calculated index
        newColumnOrder.splice(insertIndex, 0, ...missingKeys)

        // Move existing headers before 'actions' in newOrderedHeaders
        missingKeys.forEach((key) => {
          const index = newOrderedHeaders.findIndex(
            (obj) => obj.columnId === key
          )
          if (index !== -1) {
            const [item] = newOrderedHeaders.splice(index, 1)
            newOrderedHeaders.splice(insertIndex, 0, item)
          }
        })

        // Update state with new ordered headers and column order
        setOrderedHeaders(newOrderedHeaders)
        setColumnOrder(newColumnOrder)
      }

      // Mark as initialized to prevent future runs
      initializedRef.current = true
    }
  }, [orderedHeaders, columnOrder, setColumnOrder, setOrderedHeaders])

  return (
    <Modal
      open={show}
      title="Table Columns"
      onAccept={handleApply}
      acceptButtonText="Apply"
      onOpenChange={(open) => !open && setShow(false)}
      trigger={
        <TableButton
          text="Columns"
          icon={HiOutlineViewColumns}
          onClick={() => setShow(true)}
        />
      }
    >
      <div className={'max-h-[500px] overflow-auto'}>
        <OrderableList
          style={{ gap: 10, display: 'flex', flexDirection: 'column' }}
          onDragStartKey={setDraggingKey}
          onDragEndKey={() => setDraggingKey(null)}
          onOrderChangeKey={handleSortChange}
        >
          {orderedHeaders
            ? orderedHeaders!
                .filter(({ isEditable }) => isEditable)
                .filter(({ isHidden }) => !isHidden)
                .map(({ header, columnId, isVisible }) => (
                  <ColumnItem
                    key={String(columnId)}
                    isDragging={String(columnId) === draggingKey}
                    label={header}
                    isChecked={isVisible}
                    onChange={(checked) =>
                      setIsVisibleOnOrderedHeaders(columnId, checked)
                    }
                  />
                ))
            : []}
        </OrderableList>
        <div
          style={{
            marginTop: 10,
            gap: 10,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {orderedHeaders
            ?.filter(({ isEditable }) => !isEditable)
            .map(({ header, columnId, isVisible }) => (
              <ColumnItem
                key={String(columnId)}
                label={header}
                isChecked={isVisible}
                disabled
              />
            ))}
        </div>
      </div>
    </Modal>
  )
}
